/**
 * ContratarModal — wizard self-service FM (Stitch booking-hub-onboarding-v1).
 */
import { useState, useCallback, useEffect } from "react";
import { ArrowRight, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import type { SaasBankTransfer, SaasBankTransferUsd } from "@/lib/saas-billing-config";
import {
  ContratarPaymentStep,
  computePeriodTotal,
  type PayCurrency,
  type PayProvider,
} from "@/components/onboarding/ContratarPaymentStep";
import {
  OnboardingProgress,
  OnboardingStitchModal,
  OnboardingStepHeader,
  OnboardingSuccessPanel,
  OnboardingStepIndex,
  StitchBtnGhostLink,
  StitchBtnOutline,
  StitchBtnPrimary,
  StitchInfoBox,
  StitchInput,
  StitchLabel,
  StitchPasswordInput,
  StitchSelect,
} from "@/components/onboarding/OnboardingStitchUi";
import { getTenantBaseDomain } from "@/lib/tenant-dns";

type Step = "business" | "account" | "payment" | "trial_sent" | "success";

const PROFILES = [
  { id: "market",           label: "Almacén",       desc: "Productos físicos con stock, combos y delivery" },
  { id: "fast_food",        label: "Fast food",      desc: "Pedidos con comanda de cocina y delivery" },
  { id: "restaurant",       label: "Restaurante",    desc: "Carta digital, mesa y take away" },
  { id: "pharmacy",         label: "Farmacia",       desc: "Medicamentos, salud y catálogo con stock" },
  { id: "fashion",          label: "Indumentaria",   desc: "Talles, colores, ticket de cambio" },
  { id: "bazar",            label: "Bazar",          desc: "Hogar y deco, múltiples categorías" },
  { id: "catalog",          label: "Catálogo",       desc: "Solo mostrar productos, sin carrito activo" },
  { id: "dietetics",        label: "Dietética",      desc: "Granel, pesaje y productos naturales" },
  { id: "digital_products", label: "Digital",        desc: "Archivos, cursos y descargas digitales" },
];

export const PROFILE_IDS = PROFILES.map((p) => p.id) as [string, ...string[]];

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  planId: string;
  planName: string;
  priceMonthly: number;
  billingMode: "monthly_oneshot" | "annual_oneshot";
  annualDiscountPct?: number;
  bankTransferArs?: SaasBankTransfer | null;
  bankTransferUsd?: SaasBankTransferUsd | null;
  /** "payment" (default) = flujo self-service con pago. "trial" = solicitud manual. */
  mode?: "payment" | "trial";
  /** Nicho pre-seleccionado desde el CTA del pricing. Si se provee, oculta el picker de tipo de negocio. */
  businessProfile?: string;
}

interface FormState {
  currency: PayCurrency;
  businessName: string;
  businessProfile: string;
  businessSlug: string;
  ownerName: string;
  ownerEmail: string;
  ownerPassword: string;
  ownerPhone: string;
  ownerPhonePrefix: string;
  ownerDni: string;
}

const PHONE_PREFIXES = [
  { value: "+54", label: "AR +54" },
  { value: "+34", label: "ES +34" },
  { value: "+52", label: "MX +52" },
  { value: "+57", label: "CO +57" },
];

function stepIndex(step: Step): OnboardingStepIndex {
  if (step === "business") return 1;
  if (step === "account") return 2;
  if (step === "payment") return 3;
  if (step === "trial_sent") return 4;
  return 4;
}

// Cliente slugify para preview local (sin tildes, solo a-z0-9-)
function clientSlugify(s: string): string {
  return s.toLowerCase().normalize("NFD").replace(/\p{M}/gu, "")
    .replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 60);
}

export function ContratarModal({
  open, onOpenChange, planId, planName, priceMonthly, billingMode,
  annualDiscountPct = 25, bankTransferArs, mode = "payment",
  businessProfile: presetProfile,
}: Props) {
  const baseDomain = getTenantBaseDomain();
  const [step, setStep] = useState<Step>("business");
  const [form, setForm] = useState<FormState>({
    currency: "ARS",
    businessName: "", businessProfile: presetProfile ?? "market", businessSlug: "",
    ownerName: "", ownerEmail: "", ownerPassword: "", ownerPhone: "",
    ownerPhonePrefix: "+54", ownerDni: "",
  });
  const [trialSlug, setTrialSlug] = useState<string>("");
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [isManualTransfer, setIsManualTransfer] = useState(false);
  const [isPayway, setIsPayway] = useState(false);

  const post = useCallback(async (body: Record<string, unknown>) => {
    const res = await fetch("/api/self-service/session", {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
    });
    const data = (await res.json()) as Record<string, unknown>;
    if (!res.ok) throw new Error(String(data.error ?? "Error del servidor"));
    return data;
  }, []);

  useEffect(() => {
    if (!open) return;
    setStep("business");
    setForm({
      currency: "ARS", businessName: "", businessProfile: presetProfile ?? "market", businessSlug: "",
      ownerName: "", ownerEmail: "", ownerPassword: "", ownerPhone: "",
      ownerPhonePrefix: "+54", ownerDni: "",
    });
    setTrialSlug("");
    setSessionId(null); setError(null); setIsManualTransfer(false); setIsPayway(false);
    let cancelled = false;
    void (async () => {
      setSessionLoading(true);
      try {
        const data = await post({ action: "start", planId, billingMode });
        if (!cancelled) setSessionId(String(data.sessionId));
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "No se pudo iniciar la contratación");
      } finally {
        if (!cancelled) setSessionLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, planId, billingMode, post, presetProfile]);

  const onChange = useCallback((k: keyof FormState, v: string) => {
    setForm((f) => {
      const next = { ...f, [k]: v };
      // Auto-generar slug cuando el usuario escribe el nombre del negocio
      if (k === "businessName" && !f.businessSlug) {
        next.businessSlug = clientSlugify(v);
      }
      return next;
    });
    setError(null);
  }, []);

  const fullPhone = form.ownerPhone.trim()
    ? `${form.ownerPhonePrefix}${form.ownerPhone.replace(/\D/g, "")}`
    : "";

  const handleBusinessNext = async () => {
    if (!sessionId) return;
    setLoading(true); setError(null);
    try {
      const data = await post({
        action: "business_info", sessionId,
        businessName: form.businessName,
        businessProfile: form.businessProfile,
        slug: form.businessSlug || undefined,
      });
      // Sincronizar slug confirmado por el server
      if (data.slug) onChange("businessSlug", String(data.slug));
      setStep("account");
    } catch (e) { setError(e instanceof Error ? e.message : "Error"); }
    finally { setLoading(false); }
  };

  const handleTrialRequest = async () => {
    if (!sessionId) return;
    const data = await post({ action: "request_trial", sessionId });
    setTrialSlug(String(data.slug ?? form.businessSlug));
    setStep("trial_sent");
  };

  const handleAccountNext = async () => {
    if (!sessionId) return;
    setLoading(true); setError(null);
    try {
      await post({
        action: "account_info", sessionId,
        ownerName: form.ownerName, ownerEmail: form.ownerEmail,
        ownerPassword: form.ownerPassword, ownerPhone: fullPhone || undefined,
        ownerDni: form.ownerDni,
      });
      if (mode === "trial") {
        await handleTrialRequest();
      } else {
        setStep("payment");
      }
    } catch (e) { setError(e instanceof Error ? e.message : "Error"); }
    finally { setLoading(false); }
  };

  const handlePay = async (provider: PayProvider, extra?: { token?: string; bin?: string }) => {
    if (!sessionId) return;
    setLoading(true); setError(null);
    try {
      const body: Record<string, unknown> = { action: "initiate_payment", sessionId, provider, currency: "ARS" };
      if (provider === "payway" && extra?.token) { body.token = extra.token; body.bin = extra.bin; body.paymentMethodId = 1; }
      const data = await post(body);
      if ((provider === "uala" || provider === "mercadopago" || provider === "dlocal") && data.checkoutUrl) {
        window.location.href = String(data.checkoutUrl);
        return;
      }
      if (provider === "payway") { setIsPayway(true); setStep("success"); return; }
      setIsManualTransfer(true); setStep("success");
    } catch (e) { setError(e instanceof Error ? e.message : "Error al iniciar el pago"); }
    finally { setLoading(false); }
  };

  const arsTotal = computePeriodTotal(priceMonthly, billingMode, annualDiscountPct);
  const period = billingMode === "annual_oneshot" ? "/año" : "/mes";

  return (
    <OnboardingStitchModal open={open} onOpenChange={onOpenChange} brandName="Structura Market">
      {step === "success" ? (
        <OnboardingSuccessPanel
          isManual={isManualTransfer}
          isPayway={isPayway}
          businessName={form.businessName}
          onClose={() => onOpenChange(false)}
        />
      ) : (
        <div className="px-6 py-8 sm:px-12 sm:py-10">
          <OnboardingProgress step={stepIndex(step)} />

          {sessionLoading && step === "business" ? (
            <div className="flex items-center justify-center gap-2 py-16 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" /> Preparando contratación…
            </div>
          ) : (
            <>
              {step === "business" && sessionId && (
                <form className="mt-8 space-y-6" onSubmit={(e) => { e.preventDefault(); void handleBusinessNext(); }}>
                  <OnboardingStepHeader planName={mode === "trial" ? "Trial" : planName} subtitle="Tu negocio" arsTotal={arsTotal} period={period} />
                  <div>
                    <StitchLabel htmlFor="bname">Nombre del negocio</StitchLabel>
                    <StitchInput
                      id="bname" autoFocus value={form.businessName}
                      onChange={(e) => onChange("businessName", e.target.value)}
                      placeholder="Ej: Almacén La Esquina" required minLength={2}
                    />
                  </div>
                  {!presetProfile && (
                    <div>
                      <StitchLabel>Tipo de negocio</StitchLabel>
                      <p className="mb-3 text-xs text-muted-foreground">Esto configura tu menú, herramientas y la experiencia de tu tienda.</p>
                      <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                        {PROFILES.map((p) => (
                          <button
                            key={p.id}
                            type="button"
                            onClick={() => onChange("businessProfile", p.id)}
                            className={cn(
                              "rounded-xl border px-3 py-3 text-left transition-all",
                              form.businessProfile === p.id
                                ? "border-primary bg-primary/10"
                                : "border-border hover:border-primary/40",
                            )}
                          >
                            <p className={cn("text-xs font-semibold", form.businessProfile === p.id ? "text-primary" : "text-muted-foreground")}>{p.label}</p>
                            <p className="mt-0.5 text-[11px] text-muted-foreground">{p.desc}</p>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {error && <p className="text-sm text-danger">{error}</p>}
                  <StitchBtnPrimary type="submit" loading={loading}>
                    Continuar <ArrowRight className="h-5 w-5" />
                  </StitchBtnPrimary>
                  <StitchBtnGhostLink type="button" onClick={() => onOpenChange(false)}>Cancelar y volver</StitchBtnGhostLink>
                </form>
              )}

              {step === "account" && (
                <form className="mt-8 space-y-6" onSubmit={(e) => { e.preventDefault(); void handleAccountNext(); }}>
                  <OnboardingStepHeader planName={planName} subtitle="Tu cuenta de acceso" arsTotal={arsTotal} period={period} centered />
                  <div className="space-y-2">
                    <StitchLabel htmlFor="oname">Nombre completo</StitchLabel>
                    <StitchInput id="oname" autoFocus value={form.ownerName} onChange={(e) => onChange("ownerName", e.target.value)} required minLength={2} />
                  </div>
                  <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    <div className="space-y-2">
                      <StitchLabel htmlFor="oemail">Email</StitchLabel>
                      <StitchInput id="oemail" type="email" value={form.ownerEmail} onChange={(e) => onChange("ownerEmail", e.target.value)} required />
                    </div>
                    <div className="space-y-2">
                      <StitchLabel htmlFor="odni">DNI</StitchLabel>
                      <StitchInput id="odni" inputMode="numeric" value={form.ownerDni} onChange={(e) => onChange("ownerDni", e.target.value.replace(/\D/g, ""))} placeholder="12345678" required minLength={7} maxLength={11} />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <StitchLabel htmlFor="ophone">Teléfono</StitchLabel>
                    <div className="flex gap-3">
                      <StitchSelect className="w-32 shrink-0" value={form.ownerPhonePrefix} onChange={(e) => onChange("ownerPhonePrefix", e.target.value)}>
                        {PHONE_PREFIXES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                      </StitchSelect>
                      <StitchInput id="ophone" type="tel" className="flex-1" value={form.ownerPhone} onChange={(e) => onChange("ownerPhone", e.target.value)} placeholder="11 1234 5678" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <StitchLabel htmlFor="opw">Contraseña</StitchLabel>
                    <StitchPasswordInput id="opw" value={form.ownerPassword} onChange={(v) => onChange("ownerPassword", v)} required />
                  </div>
                  {error && <p className="text-sm text-danger">{error}</p>}
                  <div className="flex flex-col gap-4 pt-2 md:flex-row">
                    <StitchBtnOutline type="button" className="flex-1" onClick={() => setStep("business")}>Atrás</StitchBtnOutline>
                    <StitchBtnPrimary type="submit" className="md:flex-[2]" loading={loading}>
                      {mode === "trial" ? "Solicitar acceso gratuito" : "Continuar al pago"}
                    </StitchBtnPrimary>
                  </div>
                  <StitchBtnGhostLink type="button" onClick={() => onOpenChange(false)}>Cancelar</StitchBtnGhostLink>
                </form>
              )}

              {step === "trial_sent" && (
                <div className="mt-8 flex flex-col items-center gap-6 text-center">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/15">
                    <svg className="h-8 w-8 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-foreground">¡Solicitud enviada!</h2>
                    <p className="mt-2 text-sm text-muted-foreground">
                      Recibimos tu pedido para <span className="font-semibold text-muted-foreground">{form.businessName}</span>.
                      Te avisaremos por email cuando tu tienda esté lista.
                    </p>
                  </div>
                  {trialSlug && (
                    <div className="w-full rounded-xl border border-border bg-card px-4 py-3 text-left">
                      <p className="text-[11px] text-muted-foreground">Tu dirección web será:</p>
                      <p className="mt-1 text-sm font-mono font-semibold text-primary">
                        https://{trialSlug}.{baseDomain}
                      </p>
                    </div>
                  )}
                  <StitchInfoBox>
                    Una vez que aprobemos tu cuenta, te llegará un email con el link para ingresar usando el email y contraseña que ya configuraste.
                  </StitchInfoBox>
                  <StitchBtnPrimary type="button" onClick={() => onOpenChange(false)}>
                    Entendido
                  </StitchBtnPrimary>
                </div>
              )}

              {step === "payment" && sessionId && (
                <div className="mt-8">
                  <ContratarPaymentStep
                    currency="ARS"
                    priceMonthly={priceMonthly} billingMode={billingMode} annualDiscountPct={annualDiscountPct}
                    planName={planName} businessName={form.businessName} ownerDni={form.ownerDni}
                    bankTransferArs={bankTransferArs} onPay={handlePay} onBack={() => setStep("account")}
                    onCancel={() => onOpenChange(false)}
                    error={error} loading={loading}
                  />
                </div>
              )}
            </>
          )}
        </div>
      )}
    </OnboardingStitchModal>
  );
}
